import React, { useState, useEffect } from 'react';
import { usersApi } from '../../api/users.js';
import { leadsApi } from '../../api/leads.js';
import toast, { Toaster } from 'react-hot-toast';
import Select from 'react-select';

function BulkAssignModal({ selectedRep, onClose, onAssigned }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const perPage = 20;

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await leadsApi.getAll({ limit: perPage, page, search });
        const data = res.data ?? res;
        const leads = data.data ?? data;
        setCustomers(Array.isArray(leads) ? leads : []);
        setTotalCount(res.count ?? (res.data?.count) ?? leads.length);
      } catch (err) {
        console.error('Failed to fetch leads:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [page, search]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      await leadsApi.bulkAssign({ leadIds: selectedIds, assigned_to: selectedRep.id });
      toast.success(`Successfully assigned ${selectedIds.length} customers to ${selectedRep.name}`);
      onAssigned();
      onClose();
    } catch (err) {
      toast.error('Failed to assign customers');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '20px', width: '800px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Bulk Assign Customers to {selectedRep.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading customers...</div>
          ) : customers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No customers found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid var(--border-color)' }}>
                <tr>
                  <th style={{ padding: '12px', width: '40px' }}>
                    <input 
                      type="checkbox" 
                      checked={customers.length > 0 && customers.every(c => selectedIds.includes(c.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newIds = customers.map(c => c.id).filter(id => !selectedIds.includes(id));
                          setSelectedIds([...selectedIds, ...newIds]);
                        } else {
                          const customerIds = customers.map(c => c.id);
                          setSelectedIds(selectedIds.filter(id => !customerIds.includes(id)));
                        }
                      }}
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)' }}>NAME</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)' }}>PHONE</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: 'var(--text-muted)' }}>CURRENT ASSIGNEE</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px' }}>
                      <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} />
                    </td>
                    <td style={{ padding: '12px', fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>{c.phone}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
                      {c.Assignee?.name || (c.assigned_to ? `User #${c.assigned_to}` : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '0.5rem', alignItems: 'center' }}>
            <button
              className="btn btn-sm btn-outline"
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline"
              disabled={page === totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, alignSelf: 'center' }}>
            {selectedIds.length} selected
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAssign} disabled={saving || selectedIds.length === 0}>
              {saving ? 'Assigning...' : `Assign ${selectedIds.length} Customers`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SalesRepCustomersPage({ user }) {
  const [salesReps, setSalesReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);

  useEffect(() => {
    const fetchReps = async () => {
      try {
        const params = { role: 'sales', limit: 100 };
        // If current user is a Team Lead, only show their own assigned reps
        if (user?.role === 'sales_tl' || user?.role === 'teamleader') {
          params.tl_id = user.id; // or user.userId depending on payload
        }
        const res = await usersApi.getAll(params);
        setSalesReps((res.data ?? []).filter(u => u.is_active !== false));
      } catch (err) {
        console.error('Failed to load sales reps', err);
      }
    };
    fetchReps();
  }, [user]);

  const fetchCustomers = async (repId) => {
    setLoading(true);
    try {
      const res = await leadsApi.getAll({ assigned_to: repId, limit: 1000 }); // Large limit to show all
      const data = res.data ?? res;
      setCustomers(data.data ?? data);
    } catch (err) {
      console.error('Failed to load customers', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRep) {
      fetchCustomers(selectedRep.id);
    } else {
      setCustomers([]);
    }
  }, [selectedRep]);

  return (
    <div className="page-body">
      <Toaster position="top-right" />
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="card-title">Team Customers</span>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>View and manage customers for your sales reps</p>
          </div>
          {selectedRep && (
            <button className="btn btn-primary btn-sm" onClick={() => setIsBulkAssignOpen(true)}>
              + Bulk Assign Customers
            </button>
          )}
        </div>
        <div className="card-body">
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ width: '300px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>Select Sales Rep</label>
              <Select
                options={salesReps.map(rep => ({ value: rep.id, label: `${rep.name} (${rep.email})`, repData: rep }))}
                value={selectedRep ? { value: selectedRep.id, label: `${selectedRep.name} (${selectedRep.email})`, repData: selectedRep } : null}
                onChange={(selectedOption) => {
                  setSelectedRep(selectedOption ? selectedOption.repData : null);
                }}
                isClearable
                placeholder="-- Choose a Sales Rep --"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '38px',
                    borderColor: '#ced4da',
                    borderRadius: '4px'
                  })
                }}
              />
            </div>
            
            {loading && <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading customers...</div>}
          </div>

          {!selectedRep ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
              Please select a sales rep to view their assigned customers.
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '35%' }}>Customer Name</th>
                    <th style={{ width: '30%' }}>Email</th>
                    <th style={{ width: '20%' }}>Phone</th>
                    <th style={{ width: '15%' }}>City</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                        No customers assigned to this sales rep.
                      </td>
                    </tr>
                  ) : customers.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{c.email || '—'}</td>
                      <td>{c.phone || '—'}</td>
                      <td style={{ color: 'var(--text-muted)' }}>
                        {c.locations?.[0]?.city || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isBulkAssignOpen && (
        <BulkAssignModal 
          selectedRep={selectedRep} 
          onClose={() => setIsBulkAssignOpen(false)} 
          onAssigned={() => fetchCustomers(selectedRep.id)}
        />
      )}
    </div>
  );
}
